import { showNightTasks } from '../../../../shared/services/nightTasks.ts';
import { AutoTask } from '../../../../shared/types/autoTask.ts';
import { AutoTaskRow, CombinedDisconnects, MachineStatusRow, TaskRow } from './autoTaskRow.ts';
import { combineFixTasks } from './combineFixTasks.ts';
import { getEquipmentParts } from './getEquipmentParts.ts';
import { getTimeRemaining } from './getTimeRemaining.ts';
import { sortAutoTasks } from './sortAutoTasks.ts';


/** Prepares automatic task rows and grouped machine-status details for display. */
export function useAutoTaskRows(tasks: AutoTask[]) {
    const nightTasksVisible = showNightTasks();
    const rows: AutoTaskRow[] = tasks
        .filter((task) => task.type !== 'disconnected' && task.type !== 'fixInProgress')
        .map((task) => prepareTaskRow(task, nightTasksVisible));

    const disconnectedTasks = tasks.filter((task) => task.type === 'disconnected');
    const disconnectedSummary = combineDisconnects(disconnectedTasks);
    if (disconnectedSummary) {
        rows.push(disconnectedSummary);
    }

    const fixTasks = tasks.filter((task) => task.type === 'fixInProgress');
    const fixSummary = combineFixTasks(fixTasks);
    if (fixSummary) {
        rows.push(fixSummary);
    }

    sortAutoTasks(rows);
    return { rows, disconnectedSummary, fixSummary };
}

function prepareTaskRow(task: AutoTask, nightTasksVisible: boolean): TaskRow {
    const { coordinates, name } = getEquipmentParts(task);
    return {
        kind: 'task',
        task,
        coordinates,
        name,
        timeRemaining: getTimeRemaining(task),
        notes: getTaskNotes(task),
        highlightClass: getHighlightClass(task, nightTasksVisible),
    };
}

function combineDisconnects(tasks: AutoTask[]): CombinedDisconnects | null {
    if (tasks.length === 0) {
        return null;
    }

    const machines: MachineStatusRow[] = tasks.map((task) => {
        const { coordinates, name } = getEquipmentParts(task);
        return {
            equipment: task.equipment,
            coordinates,
            name: name || task.equipment,
            duration: Math.abs(getTimeRemaining(task)),
        };
    });
    machines.sort((left, right) => left.duration - right.duration);

    const coordinates = [...new Set(machines.map((machine) => machine.coordinates)
        .filter((value) => value.length > 0))];
    const previewLimit = 5;
    const coordinatesPreview = coordinates.slice(0, previewLimit).join(', ');
    const coordinatesRemainder = coordinates.length - previewLimit;

    return {
        kind: 'disconnectedSummary',
        tasks: machines,
        coordinatesPreview,
        coordinatesRemainder,
        highlightClass: 'highlight--issue',
    };
}

function getHighlightClass(task: AutoTask, nightTasksVisible: boolean): string {
    const highlightIssue =
        task.type === 'help' ||
        task.type === 'disconnected' ||
        (task.type === 'workCounter' && task.isTaskUp) ||
        (task.type === 'toolChange' && task.isTaskUp);
    if (highlightIssue) {
        return 'highlight--issue';
    }

    const highlightNight = nightTasksVisible && task.isRequiredThisNight;
    if (highlightNight) {
        return 'highlight--night';
    }
    return '';
}

function getTaskNotes(task: AutoTask): string {
    switch (task.type) {
        case 'help': {
            const needsMeasuring = task.needsMeasuring ? ' · M' : '';
            return 'Help me' + needsMeasuring;
        }
        case 'disconnected':
            return 'Disconnected';
        case 'measure': {
            const partsToMeasure = task.partsToMeasure && task.partsToMeasure > 1
                ? ` ${task.partsToMeasure}pcs` : '';
            const nextVariant = task.nextVariant && task.nextVariant.length > 0
                ? ` (${task.nextVariant})` : '';
            const message = 'Measure' + nextVariant + partsToMeasure;
            return message;
        }
        case 'workCounter':
            return 'Work counter';
        case 'toolChange': {
            const count = task.toolChangeCount && task.toolChangeCount > 1
                ? ` x${task.toolChangeCount}` : '';
            const message = 'Tool change' + count;
            return message;
        }
        default:
            return '';
    }
}
